import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { useI18n } from "../../hooks/useI18n";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchBrandNames } from "../../store/tenants/actions";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { DistributorId } from "../../models/Product";
import { AppState } from "../../store";

interface BrandNameMultiSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected?: (value: ReadonlyArray<string>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<string>;
}

export const BrandNameMultiSelect: FunctionComponent<
  BrandNameMultiSelectProps
> = ({
  label,
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const { t } = useI18n();
  const defaultLabel = label || t("common.formFields.brand");
  const brandNames = useSelector<AppState, ReadonlyArray<string>>(
    s => s.tenants.brandNames
  );
  const [state, setState] = useState<ReadonlyArray<string>>([]);

  useLoadIfEmpty(!brandNames?.length, thunkFetchBrandNames);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let before = event.target.value as Array<string>;
      let after = state.filter(x => before.includes(x));

      onSelected(after ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<DistributorId>): ReactNode => {
    return state
      .filter(x => ids.includes(x))
      .map(x => x)
      .join(", ");
  };

  useEffect(() => {
    let filtered = brandNames && brandNames.length > 0 && brandNames;

    if (filtered) {
      setState(filtered);
    }
  }, [brandNames]);

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="brand-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {defaultLabel}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="brand-multi-select-label"
        id="brand-multi-select"
        autoWidth={true}
        input={<Input />}
        renderValue={value => renderNames(value as string[])}
      >
        {state &&
          state.length > 0 &&
          state.map(x => (
            <MenuItem key={x} value={x}>
              <Checkbox checked={value.indexOf(x) > -1} />
              <ListItemText primary={x} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
